import React, {
  ReactElement,
  useCallback,
  useMemo,
  useReducer,
  useContext,
  createContext,
} from 'react';

import { TChildrenOnly } from 'types/common';
import { TWizardContext } from './WizardProvider.types';
import { ACTION_TYPES, INITIAL_STATE } from './WizardProvider.constants';
import wizardReducer from './WizardProvider.reducer';

const WizardContext = createContext<TWizardContext | undefined>(undefined);

export function WizardProvider({ children }: TChildrenOnly): ReactElement {
  const [state, dispatch] = useReducer(wizardReducer, INITIAL_STATE);

  const setMobileTLInputs = useCallback(
    (mobileTLProps) => {
      dispatch({
        type: ACTION_TYPES.ADD_MOBILE_TL_INPUTS,
        payload: mobileTLProps,
      });
    },
    [dispatch]
  );

  const setMobilePackageInputs = useCallback(
    (mobilePackageProps) => {
      dispatch({
        type: ACTION_TYPES.ADD_MOBILE_PACKAGE_INPUTS,
        payload: mobilePackageProps,
      });
    },
    [dispatch]
  );

  const setReferenceId = useCallback(
    (referenceId) => {
      dispatch({
        type: ACTION_TYPES.ADD_REFERENCE_ID,
        payload: referenceId,
      });
    },
    [dispatch]
  );

  const setWalletGiftCodeInputs = useCallback(
    (giftCodeInputs) => {
      dispatch({
        type: ACTION_TYPES.ADD_WALLET_GIFT_CODE_INPUTS,
        payload: giftCodeInputs,
      });
    },
    [dispatch]
  );

  const clearMobileTLInputs = useCallback(() => {
    dispatch({
      type: ACTION_TYPES.CLEAR_MOBILE_TL_INPUTS,
      payload: INITIAL_STATE.mobileTLInputs,
    });
  }, [dispatch]);

  const clearMobilePackageInputs = useCallback(() => {
    dispatch({
      type: ACTION_TYPES.CLEAR_MOBILE_PACKAGE_INPUTS,
      payload: INITIAL_STATE.mobilePackageInputs,
    });
  }, [dispatch]);
  const setRaffleLandingModalViewsIncreased = useCallback(() => {
    dispatch({
      type: ACTION_TYPES.SET_RAFFLE_LANDING_MODAL_VIEWS_INCREASED,
      payload: true,
    });
  }, [dispatch]);

  const setPreOrderId = useCallback(
    (preOrderId) => {
      dispatch({
        type: ACTION_TYPES.ADD_PREORDER_ID,
        payload: preOrderId,
      });
    },
    [dispatch]
  );
  const value = useMemo(
    () => ({
      ...state,
      setMobileTLInputs,
      setMobilePackageInputs,
      setReferenceId,
      setWalletGiftCodeInputs,
      clearMobileTLInputs,
      clearMobilePackageInputs,
      setRaffleLandingModalViewsIncreased,
      setPreOrderId,
    }),
    [
      state,
      setMobileTLInputs,
      setMobilePackageInputs,
      setReferenceId,
      setWalletGiftCodeInputs,
      clearMobileTLInputs,
      clearMobilePackageInputs,
      setRaffleLandingModalViewsIncreased,
      setPreOrderId,
    ]
  );

  return (
    <WizardContext.Provider value={value}>{children}</WizardContext.Provider>
  );
}

export function useWizard(): TWizardContext {
  const context = useContext(WizardContext);
  if (context === undefined) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
}
